import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  X, Heart, Star, ShoppingBag, Minus, Plus,
  Truck, Shield, Clock, ChevronLeft, ChevronRight,
  User, Package, MessageCircle, BadgeCheck, Calendar,
  MapPin, Car, Store, Sparkles, Check,
} from 'lucide-react';

const ItemDetailModal = ({
  type,
  item,
  onClose,
  onAddToCart,
  onBook,
  onToggleFavorite,
  isFavorite,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isService = type === 'service';
  const isProduct = type === 'product';

  const [quantity, setQuantity] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [added, setAdded] = useState(false);

  // ---------- Media resolution ----------
  const media = useMemo(() => {
    if (!item) return [];
    if (Array.isArray(item.media) && item.media.length > 0) return item.media;
    if (item.media?.url) return [item.media];
    if (item.mediaLegacy?.url) return [item.mediaLegacy];
    if (item.image) return [{ url: item.image, type: 'image' }];
    return [];
  }, [item]);

  const current = media[currentIndex];

  // Auto-select the first option for services
  useEffect(() => {
    if (isService && Array.isArray(item?.options) && item.options.length > 0) {
      setSelectedOption(item.options[0]);
    } else {
      setSelectedOption(null);
    }
    // Reset add-ons whenever the item changes
    setSelectedAddons([]);
  }, [item?.id, isService]);

  // Reset on item change
  useEffect(() => {
    setQuantity(1);
    setCurrentIndex(0);
    setSelectedSize(null);
    setSelectedColor(null);
    setAdded(false);
  }, [item?.id]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft' && media.length > 1) {
        setCurrentIndex((i) => (i - 1 + media.length) % media.length);
      }
      if (e.key === 'ArrowRight' && media.length > 1) {
        setCurrentIndex((i) => (i + 1) % media.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [media.length, onClose]);

  if (!item) return null;

  // ---------- Derived ----------
  const displayName = isService ? item.title : item.name;
  const displayDescription = item.description;
  const basePrice = isService
    ? (selectedOption ? Number(selectedOption.price) : Number(item.price) || 0)
    : Number(item.price) || 0;

  // Sum of selected add-ons
  const addonsTotal = selectedAddons.reduce((sum, a) => sum + Number(a.price || 0), 0);

  const price = basePrice + addonsTotal;
  const duration = isService
    ? (selectedOption?.duration || item.duration || '45 min')
    : null;
  const stock = Number(item.stock) || 0;
  const isOut = isProduct && stock === 0;
  const isLow = isProduct && stock > 0 && stock < 5;
  const maxQty = isProduct ? Math.max(1, stock || 1) : 1;
  const hasOptions = isService && Array.isArray(item.options) && item.options.length > 0;
  const hasAddons = isService && Array.isArray(item.addons) && item.addons.length > 0;
  const hasSizes = isProduct && Array.isArray(item.sizes) && item.sizes.length > 0;
  const hasColors = isProduct && Array.isArray(item.colors) && item.colors.length > 0;
  const totalPrice = price * quantity;

  // Check if logged-in user is the owner
  const isOwner = user?.uid && item.professionalId === user.uid;

  const getLocationLabel = () => {
    const mode = item.locationMode || 'salon';
    if (mode === 'mobile') return 'Pro travels to you';
    if (mode === 'both') return 'Salon or mobile';
    return 'At the pro';
  };

  const getLocationIcon = () => {
    const mode = item.locationMode || 'salon';
    if (mode === 'mobile') return <Car size={11} />;
    if (mode === 'both') return <MapPin size={11} />;
    return <Store size={11} />;
  };

  // ---------- Handlers ----------
  const handleQuantityChange = (delta) => {
    setQuantity((q) => Math.max(1, Math.min(maxQty, q + delta)));
  };

  const toggleAddon = (addon) => {
    setSelectedAddons((prev) => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) return prev.filter(a => a.id !== addon.id);
      return [...prev, addon];
    });
  };

  const isAddonSelected = (addon) =>
    selectedAddons.some(a => a.id === addon.id);

  const handleAddToCart = () => {
    if (isOut || isOwner) return;
    if (onAddToCart) {
      onAddToCart(item, quantity);
      setAdded(true);
      setTimeout(() => setAdded(false), 1600);
    }
  };

  const handleBook = () => {
    if (isOwner) {
      alert("You cannot book your own service.");
      return;
    }
    if (onBook) {
      onBook({
        ...item,
        _selectedOption: selectedOption,
        _selectedAddons: selectedAddons,
        _basePrice: basePrice,
        _addonsTotal: addonsTotal,
        _totalPrice: price,
        _totalDuration: duration,
      });
    }
  };

  const handleFavorite = () => {
    if (onToggleFavorite) onToggleFavorite(item.id);
  };

  const nextImage = () => setCurrentIndex((i) => (i + 1) % media.length);
  const prevImage = () => setCurrentIndex((i) => (i - 1 + media.length) % media.length);

  const handleViewPro = () => {
    if (!item.professionalId) return;
    onClose?.();
    navigate(`/professional/${item.professionalId}`);
  };

  const handleMessagePro = () => {
    if (!item.professionalId) return;
    if (!user) {
      onClose?.();
      navigate('/login');
      return;
    }
    // Prevent messaging yourself
    if (isOwner) {
      alert("You cannot message yourself.");
      return;
    }
    onClose?.();
    navigate(`/dashboard/chat?with=${item.professionalId}`);
  };

  const formatPrice = (n) => `R${Number(n || 0).toFixed(2)}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile header */}
        <div className="sm:hidden flex justify-between items-center p-4 border-b border-gray-50 shrink-0 bg-white">
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center"
          >
            <X size={18} />
          </button>
          <h2 className="text-sm font-bold text-gray-900">
            {isService ? 'Service Details' : 'Product Details'}
          </h2>
          <button
            onClick={handleFavorite}
            className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center"
          >
            <Heart size={16} className={isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto sm:overflow-hidden">
          <div className="sm:flex sm:flex-row sm:h-full sm:max-h-[95vh]">

            {/* ==================== IMAGE GALLERY ==================== */}
            <div className="w-full sm:w-1/2 bg-[#FCF8F9] relative flex flex-col shrink-0">
              <div className="relative w-full aspect-square sm:aspect-auto sm:flex-1 bg-[#F8F9FA] overflow-hidden flex items-center justify-center">
                {current ? (
                  current.type === 'video' ? (
                    <video src={current.url} className="w-full h-full object-contain" controls playsInline />
                  ) : (
                    <img src={current.url} alt={displayName} className="w-full h-full object-contain" />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {isService ? (
                      <Sparkles size={64} className="text-[#EFA9B6]" />
                    ) : (
                      <ShoppingBag size={64} className="text-gray-300" />
                    )}
                  </div>
                )}

                <button
                  onClick={onClose}
                  className="hidden sm:flex absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow-sm z-20"
                >
                  <X size={18} />
                </button>

                <button
                  onClick={handleFavorite}
                  className="hidden sm:flex absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow-sm z-20"
                >
                  <Heart size={18} className={isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'} />
                </button>

                {isService && (
                  <div className="absolute top-4 left-4 sm:left-auto sm:right-16 bg-[#FFF5F6] text-[#EFA9B6] text-[10px] font-bold px-3 py-1 rounded-full z-10 flex items-center gap-1">
                    <Sparkles size={11} />
                    Service
                  </div>
                )}

                {media.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow-md z-10"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow-md z-10"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </>
                )}

                {isOut && (
                  <div className="absolute bottom-3 left-3 bg-[#F4EEFF] text-[#AF8BD4] text-[10px] font-bold px-3 py-1 rounded-full">
                    Sold Out
                  </div>
                )}
                {isLow && (
                  <div className="absolute bottom-3 left-3 bg-[#FFF3E8] text-[#B87333] text-[10px] font-bold px-3 py-1 rounded-full">
                    Only {stock} left
                  </div>
                )}

                {media.length > 1 && (
                  <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur text-gray-800 text-[11px] font-bold px-3 py-1 rounded-full">
                    {currentIndex + 1} / {media.length}
                  </div>
                )}

                {media.length > 1 && (
                  <div className="sm:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {media.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        className={`rounded-full transition-all ${
                          idx === currentIndex
                            ? 'w-5 h-1.5 bg-[#EFA9B6]'
                            : 'w-1.5 h-1.5 bg-[#EFA9B6]/40'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {media.length > 1 && (
                <div className="hidden sm:flex gap-2 p-3 border-t border-gray-100 bg-white overflow-x-auto hide-scrollbar shrink-0">
                  {media.map((m, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-14 h-14 rounded-lg overflow-hidden shrink-0 border-2 transition-colors ${
                        idx === currentIndex ? 'border-[#EFA9B6]' : 'border-transparent hover:border-gray-200'
                      }`}
                    >
                      {m.type === 'video' ? (
                        <div className="w-full h-full bg-[#FCF8F9] flex items-center justify-center">
                          <Package size={14} className="text-gray-400" />
                        </div>
                      ) : (
                        <img src={m.url} alt="" className="w-full h-full object-cover" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ==================== DETAILS ==================== */}
            <div className="w-full sm:w-1/2 flex flex-col max-h-[95vh] min-h-0">

              <div className="flex-1 overflow-y-auto p-5 sm:p-6">

                {/* Category / Brand */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {item.category && (
                    <span className="text-[10px] font-bold text-[#EFA9B6] uppercase tracking-wider">
                      {item.category}
                    </span>
                  )}
                  {isProduct && item.brand && (
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      · {item.brand}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h1 className="text-2xl font-serif font-bold text-gray-900 leading-tight">
                  {displayName}
                </h1>

                {/* Rating */}
                {(item.rating || item.ratingCount) && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          size={13}
                          className={
                            n <= Math.round(item.rating || 0)
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-gray-200'
                          }
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">
                      {item.rating ? Number(item.rating).toFixed(1) : '0'}
                      {item.ratingCount ? ` (${item.ratingCount})` : ''}
                    </span>
                  </div>
                )}

                {/* Price — with breakdown if add-ons selected */}
                <div className="mt-4 mb-4">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-2xl sm:text-3xl font-serif font-bold text-gray-900">
                      {hasOptions && item.variablePrice ? 'From ' : ''}
                      {formatPrice(price)}
                    </span>
                    {isProduct && item.originalPrice && item.originalPrice > item.price && (
                      <span className="text-sm text-gray-400 line-through">
                        {formatPrice(item.originalPrice)}
                      </span>
                    )}
                    {isService && duration && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock size={12} />
                        {duration}
                      </span>
                    )}
                  </div>

                  {addonsTotal > 0 && (
                    <div className="mt-2 text-[11px] text-gray-500 space-y-0.5">
                      <div className="flex justify-between">
                        <span>Base price</span>
                        <span className="font-bold text-gray-700">{formatPrice(basePrice)}</span>
                      </div>
                      {selectedAddons.map(a => (
                        <div key={a.id} className="flex justify-between text-[#AF8BD4]">
                          <span>+ {a.name}</span>
                          <span className="font-bold">{formatPrice(a.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Service meta badges */}
                {isService && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 bg-[#FFF5F6] text-[#EFA9B6] text-[11px] font-bold px-3 py-1 rounded-full">
                      {getLocationIcon()}
                      {getLocationLabel()}
                    </span>
                    {hasOptions && (
                      <span className="inline-flex items-center gap-1 bg-[#F4EEFF] text-[#AF8BD4] text-[11px] font-bold px-3 py-1 rounded-full">
                        {item.options.length} options
                      </span>
                    )}
                    {hasAddons && (
                      <span className="inline-flex items-center gap-1 bg-[#FFF5F6] text-[#EFA9B6] text-[11px] font-bold px-3 py-1 rounded-full">
                        {item.addons.length} add-ons
                      </span>
                    )}
                  </div>
                )}

                {/* Sold by */}
                {item.professionalId && (
                  <button
                    onClick={handleViewPro}
                    className="w-full mb-4 p-3 rounded-2xl bg-[#FFF5F6] hover:bg-[#FBE1E8] transition-colors flex items-center gap-3 text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden shrink-0">
                      {item.professionalImage ? (
                        <img src={item.professionalImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User size={18} className="text-[#EFA9B6]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold text-[#EFA9B6] uppercase tracking-wider">
                        {isService ? 'Offered by' : 'Sold by'}
                      </p>
                      <p className="text-sm font-bold text-gray-900 truncate flex items-center gap-1">
                        {item.professionalName || item.pro?.businessName || 'Professional'}
                        <BadgeCheck size={13} className="text-[#EFA9B6] shrink-0" />
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-[#EFA9B6] shrink-0" />
                  </button>
                )}

                {/* ==================== SERVICE OPTIONS ==================== */}
                {isService && hasOptions && (
                  <div className="mb-5">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
                      Choose an option
                    </p>
                    <div className="space-y-2">
                      {item.options.map((opt) => {
                        const isSelected = selectedOption?.id === opt.id || selectedOption?.name === opt.name;
                        return (
                          <button
                            key={opt.id || opt.name}
                            onClick={() => setSelectedOption(opt)}
                            className={`w-full text-left p-3 rounded-2xl border transition-colors flex items-center gap-3 ${
                              isSelected
                                ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                                : 'border-gray-100 hover:border-[#FBE1E8]'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                              isSelected ? 'border-[#EFA9B6] bg-[#EFA9B6]' : 'border-gray-300'
                            }`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm text-gray-900 truncate">{opt.name}</p>
                              {opt.duration && (
                                <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                                  <Clock size={10} />
                                  {opt.duration}
                                </p>
                              )}
                            </div>
                            <span className="font-bold text-sm text-gray-900 shrink-0">
                              {formatPrice(opt.price)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ==================== SERVICE ADD-ONS ==================== */}
                {isService && hasAddons && (
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Extras
                      </p>
                      <span className="text-[10px] text-gray-400 font-medium">
                        Optional
                      </span>
                    </div>
                    <div className="space-y-2">
                      {item.addons.map((addon) => {
                        const isSel = isAddonSelected(addon);
                        return (
                          <button
                            key={addon.id}
                            onClick={() => toggleAddon(addon)}
                            className={`w-full text-left p-3 rounded-2xl border transition-colors flex items-center gap-3 ${
                              isSel
                                ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                                : 'border-gray-100 hover:border-[#E0D4F5]'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                              isSel ? 'border-[#AF8BD4] bg-[#AF8BD4]' : 'border-gray-300'
                            }`}>
                              {isSel && <Check size={12} className="text-white" strokeWidth={3} />}
                            </div>
                            <p className="flex-1 min-w-0 font-bold text-sm text-gray-900 truncate">
                              {addon.name}
                            </p>
                            <span className={`font-bold text-sm shrink-0 ${
                              isSel ? 'text-[#AF8BD4]' : 'text-gray-900'
                            }`}>
                              + {formatPrice(addon.price)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Description */}
                {displayDescription ? (
                  <div className="mb-5">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
                      Description
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                      {displayDescription}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic mb-5">No description provided.</p>
                )}

                {/* Ingredients (product only) */}
                {isProduct && item.ingredients && (
                  <div className="mb-5">
                    <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
                      Ingredients
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                      {item.ingredients}
                    </p>
                  </div>
                )}

                {/* Product size options */}
                {isProduct && hasSizes && (
                  <div className="mb-4">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
                      Size
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {item.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                            selectedSize === size
                              ? 'bg-[#FFF5F6] text-[#EFA9B6] border border-[#EFA9B6]'
                              : 'bg-[#FCF8F9] text-gray-600 border border-gray-100 hover:border-gray-300'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product color options */}
                {isProduct && hasColors && (
                  <div className="mb-4">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
                      Color
                    </p>
                    <div className="flex gap-2">
                      {item.colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            selectedColor === color ? 'border-[#EFA9B6]' : 'border-gray-200 hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity (product only) */}
                {isProduct && (
                  <div className="flex items-center gap-3 mb-2">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                      Quantity
                    </p>
                    <div className="flex items-center gap-2 bg-[#FCF8F9] rounded-full border border-gray-100 px-1.5">
                      <button
                        onClick={() => handleQuantityChange(-1)}
                        disabled={quantity <= 1}
                        className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#EFA9B6] disabled:opacity-40"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="font-bold text-sm text-gray-900 min-w-[20px] text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(1)}
                        disabled={quantity >= maxQty}
                        className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#EFA9B6] disabled:opacity-40"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    {item.stock !== undefined && (
                      <span className="text-xs text-gray-400">
                        {isOut ? 'Out of stock' : `${stock} in stock`}
                      </span>
                    )}
                  </div>
                )}

                {/* Trust badges */}
                <div className="grid grid-cols-3 gap-2 mt-5">
                  <div className="flex flex-col items-center text-center p-3 bg-[#FCF8F9] rounded-xl">
                    <Truck size={16} className="text-[#EFA9B6] mb-1" />
                    <p className="text-[10px] font-medium text-gray-600">
                      {isService ? 'On-site' : 'Free Ship'}
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center p-3 bg-[#FCF8F9] rounded-xl">
                    <Shield size={16} className="text-[#EFA9B6] mb-1" />
                    <p className="text-[10px] font-medium text-gray-600">
                      {isService ? 'Verified' : 'Returns'}
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center p-3 bg-[#FCF8F9] rounded-xl">
                    <Clock size={16} className="text-[#EFA9B6] mb-1" />
                    <p className="text-[10px] font-medium text-gray-600">
                      {isService ? 'Flexible' : 'Fast Delivery'}
                    </p>
                  </div>
                </div>
              </div>

              {/* ==================== FOOTER ==================== */}
              <div className="p-4 sm:p-5 border-t border-gray-50 bg-white shrink-0 space-y-2">

                {/* Service — Book Now */}
                {isService && (
                  !isOwner ? (
                    <button
                      onClick={handleBook}
                      className="w-full py-4 rounded-full font-bold text-sm transition-colors flex items-center justify-center gap-2 bg-[#FFF5F6] text-[#EFA9B6] hover:bg-[#FBE1E8]"
                    >
                      <Calendar size={16} />
                      Book Now · {formatPrice(price)}
                    </button>
                  ) : (
                    <div className="w-full py-4 rounded-full font-bold text-sm bg-gray-50 text-gray-400 text-center">
                      You offer this service
                    </div>
                  )
                )}

                {/* Product — Add to Cart */}
                {isProduct && (
                  !isOwner ? (
                    <button
                      onClick={handleAddToCart}
                      disabled={isOut}
                      className={`w-full py-4 rounded-full font-bold text-sm transition-colors flex items-center justify-center gap-2 ${
                        isOut
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : added
                          ? 'bg-green-500 text-white'
                          : 'bg-[#FFF5F6] text-[#EFA9B6] hover:bg-[#FBE1E8]'
                      }`}
                    >
                      {added ? (
                        <>✓ Added to Cart</>
                      ) : (
                        <>
                          <ShoppingBag size={16} />
                          {isOut ? 'Sold Out' : `Add to Cart · ${formatPrice(totalPrice)}`}
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="w-full py-4 rounded-full font-bold text-sm bg-gray-50 text-gray-400 text-center">
                      You sell this product
                    </div>
                  )
                )}

                {/* Message seller */}
                {item.professionalId && (
                  !isOwner && (
                    <button
                      onClick={handleMessagePro}
                      className="w-full py-3 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-xs sm:text-sm hover:bg-[#F4EEFF] transition-colors flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={14} />
                      Message {(item.professionalName || item.pro?.businessName)?.split(' ')[0] || 'seller'}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
};

export default ItemDetailModal;